/* eslint-disable react-hooks/exhaustive-deps */
import { useLayoutSplashScreen } from '@/_metronic/layout/core/_LayoutSplashScreenProvider';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { type FC, useContext, useEffect, useMemo, useState } from 'react';
import { type WithChildren } from '../../../../_metronic/helpers';
import { ApiResponseError } from '../../../../api/core/_models';
import { AUTH_EVENT_KEY } from '../../../consts/Auth';
import { type AccountDetailsDto } from '../../../models/AccountDetailsDto';
import { type AuthModel } from './_models';
import { getUserPreferencesDetails } from './_requests';
import * as authHelper from './AuthHelpers';
// The context instance + its types live in ./AuthContext so the createContext call stays stable
// across Vite HMR (see that file's header). Re-exported below for back-compat with existing imports.
import { AuthContext } from './AuthContext';

const useAuth = () => {
  return useContext(AuthContext);
};

const AuthProvider: FC<WithChildren> = ({ children }) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth());
  const [currentUser, setCurrentUser] = useState<AccountDetailsDto | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth);
    setErrorMessage(undefined);
    if (auth) {
      authHelper.setAuth(auth);
    } else {
      authHelper.removeAuth();
    }
  };

  const logout = (errorMessage?: string) => {
    // setLayout(getLayoutConfigForUserType(undefined));
    saveAuth(undefined);
    setCurrentUser(undefined);
    // Guard against callers wiring this as an event handler (e.g. onClick={logout}),
    // which would otherwise store a non-string and crash when rendered as a child.
    setErrorMessage(typeof errorMessage === 'string' ? errorMessage : undefined);
  };

  const contextValue = useMemo(
    () => ({
      auth,
      saveAuth,
      currentUser,
      setCurrentUser,
      logout,
      errorMessage,
    }),
    [auth, currentUser, logout],
  );

  //listen window event AUTH_EVENT_KEY
  useEffect(() => {
    const handleAuthChange = () => {
      const newAuth = authHelper.getAuth();
      setAuth(newAuth);
    };

    globalThis.addEventListener(AUTH_EVENT_KEY, handleAuthChange);
    return () => {
      globalThis.removeEventListener(AUTH_EVENT_KEY, handleAuthChange);
    };
  }, []);

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

type AuthInitProps = WithChildren & {
  loadingApplication?: boolean;
};

const AuthInit: FC<AuthInitProps> = ({ children, loadingApplication = false }) => {
  const { auth, logout, setCurrentUser, currentUser } = useAuth();
  const { showSplashScreen, hideSplashScreen } = useLayoutSplashScreen();

  const { data, error, refetch } = useQuery({
    queryKey: ['userDetails', auth?.token],
    queryFn: async () => (await getUserPreferencesDetails()).data,
    enabled: !!auth?.token, // token present, profile not yet loaded
    meta: { suppressGlobalError: true },
    refetchOnWindowFocus: 'always',
  });

  useEffect(() => {
    if (!auth?.token) return;
    const reloadAccount = () => {
      void refetch({ cancelRefetch: false });
    };
    globalThis.addEventListener('focus', reloadAccount);
    return () => {
      globalThis.removeEventListener('focus', reloadAccount);
    };
  }, [auth?.token, refetch]);

  const loadingProfile = !!auth?.token && !currentUser;
  const loading = loadingProfile || loadingApplication;

  useEffect(() => {
    if (loading) {
      showSplashScreen();
    } else {
      hideSplashScreen();
    }
  }, [loading]);

  useEffect(() => {
    if (!error) return;
    if (error instanceof ApiResponseError) {
      const status = error.status;
      const initialAccountDenied = status === 403 && !currentUser;
      const shouldEndSession = status === 401 || status === 500 || initialAccountDenied;
      if (shouldEndSession) {
        logout();
      }
    }
    //precisa melhorar isso aqui, um erro de rede nao deveria deslogar o usuario
    //talvez um toast de erro de conexao e tentar novamente, ao inves de deslogar direto
    if (error instanceof AxiosError) {
      const code = error.code;
      if (code == 'ERR_NETWORK') {
        logout();
      }
    }
  }, [error]);

  useEffect(() => {
    if (!data) return;
    setCurrentUser(data);
  }, [data]);

  if (loading) return null; // A página só monta quando perfil e tela de abertura estiverem prontos.

  return children;
};

export { AuthContext, AuthInit, AuthProvider, useAuth };
